import React, { createContext, CSSProperties, useContext, useEffect, useState } from "react";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import {
  filterOnlyActiveVotersInSession,
  summarizeVotes,
  timeLeftForSession,
  useAllSessions,
  useVotingSessionResults,
  useVotingSessionThumbnails,
} from "../voting-common";
import { sort } from "rambda";
import type { VotingSession, LegacyVotingSession, VoterData } from "shared/datamodel/schemas/voting-session";
import style from "./voting-panel.module.css";
import cn from "classnames";
import { OptionsContextMenu, createMenuOption } from "frontend/context-menu/optionsContextMenu";
import { getPathPrefix } from "utils/getPathPrefix";
import { useAtomValue } from "jotai";
import { userAtom } from "state-atoms";
import * as R from "rambda";
import { getReadSignedUrl, getUsersForIds } from "frontend/api";
import { sessionId } from "shared/datamodel/voting";
import { useTimeout } from "usehooks-ts";
import { useUpdate } from "react-use";
import { Thumbnail } from "./components/element-thumbnail";
import Skeleton from "react-loading-skeleton";

// sort sessions by start time - latest first
type OldAndNew = LegacyVotingSession | VotingSession;

const sortSessionsByTimestamp = sort<OldAndNew>((a, b) => b.start - a.start);

export function VotingHistory({
  syncService,
  setSession,
}: {
  syncService: SyncService<RW> | null;
  setSession: (s: OldAndNew | null) => void;
}) {
  const sessions = useAllSessions(syncService);
  const [editingTitle, setEditingTitle] = useState(0);
  const [submenuopen, setSubmenuopen] = useState<HTMLDivElement | null>(null);
  const userAtomValue = useAtomValue(userAtom);

  const myUserId = userAtomValue?.id;
  const sortedSessions = sortSessionsByTimestamp(sessions);

  // Since we sorted all sessions by start-time, the first one (which is the latest) might be
  // a session that's still running. In this case we won't show it in the list, but we'll
  // refresh this component when it's over to start showing it.
  const timeUntilActiveIsFinished = timeLeftForSession(sortedSessions[0]) || null;
  useTimeout(useUpdate(), timeUntilActiveIsFinished);

  if (timeUntilActiveIsFinished != null && timeUntilActiveIsFinished > 0) {
    // remove the ongoing voting session from the list
    sortedSessions.shift();
  }

  const options = (div: HTMLElement) => {
    if (!div.dataset.id) {
      console.warn("No data-id on the element");
      return [];
    }
    const sessionId = Number.parseInt(div.dataset.id);
    const amIOwnerOfSession = !!myUserId && sessions.find(R.propEq(sessionId, "start"))?.ownerId == myUserId;
    const onRename = () => setEditingTitle(sessionId);
    const handleDelete = () => {
      if (Number.isFinite(sessionId)) {
        syncService?.mutate.hideVotingSession({ sessionId: sessionId, hide: true });
      }
      setSubmenuopen(null);
    };

    return [
      createMenuOption({
        title: "Rename",
        iconPath: getPathPrefix("/images/pencil-icon-white.svg"),
        id: "rename",
        onClick: onRename,
      }),
      createMenuOption({
        title: "Delete",
        iconPath: getPathPrefix("/images/trash-icon.svg"),
        id: "delete",
        onClick: handleDelete,
        disabled: amIOwnerOfSession == false,
      }),
    ];
  };

  return sortedSessions.length === 0 ? (
    <NoResultsPanel />
  ) : (
    <div data-testid="voting-results" style={{ overflow: "auto" }} onScroll={() => setSubmenuopen(null)}>
      {sortedSessions.map((session: OldAndNew) => (
        <VotingShortSummary
          key={sessionId(session)}
          session={session}
          onClick={() => setSession(session)}
          onMenuOpen={(e) => {
            setSubmenuopen(e.currentTarget);
          }}
          editingTitle={editingTitle == sessionId(session)}
          usersNames={undefined}
          onChangeTitle={(title) => {
            syncService?.mutate.renameVotingSession({ sessionId: sessionId(session), newName: title });
            setEditingTitle(0);
          }}
        />
      ))}
      {submenuopen && (
        <OptionsContextMenu
          options={options(submenuopen)}
          attachToRef={{ current: submenuopen }}
          onDismiss={() => setSubmenuopen(null)}
        />
      )}
    </div>
  );
}

function NoResultsPanel() {
  return (
    <div style={{ margin: "auto" }}>
      <img
        style={{ width: "117px", display: "block", margin: "auto" }}
        src={getPathPrefix("/images/votes_no_results.png")}
        alt="No results"
      />
      <div className={style.h2} style={{ margin: "20px 0" }}>
        No votes yet, results will appear once voting is complete
      </div>
    </div>
  );
}

type UserIdInfo = { type: "known"; photo: string } | { type: "anonymous"; name: string; avatar: string };

const UsersInfoContext = createContext({} as Record<string, UserIdInfo>);

export function ShowSingleSession({
  syncService,
  session,
}: {
  syncService: SyncService<RW> | null;
  session: { start: number; title: string; anonymous: boolean };
}) {
  const results = useVotingSessionResults(syncService, sessionId(session));
  const thumbnailsUrls = useVotingSessionThumbnails(syncService, sessionId(session));

  const activeVoters = filterOnlyActiveVotersInSession(results);
  const summary = summarizeVotes(results);

  const [usersPhotos, setUsersPhotos] = useState({} as Record<string, UserIdInfo>);

  useEffect(() => {
    // recomputing, because the useEffect must depend on a stable value
    // (it checks reference equality and only results is stable!)
    const activeVoters = filterOnlyActiveVotersInSession(results);
    if (activeVoters.length === 0) return;

    // results is array of {id, votes}
    // id is either {"known", id} | {"anonymous", id, name, avatar}
    const usersInfo = {} as Record<string, UserIdInfo>;
    const idsToGet = [] as number[];
    for (const { id } of activeVoters) {
      if (id.type == "known") {
        const idNumber = Number.parseInt(id.id);
        if (Number.isFinite(idNumber)) {
          idsToGet.push(idNumber);
          usersInfo[id.id] = { type: "known", photo: "" };
        }
      } else {
        usersInfo[id.id] = { type: "anonymous", name: id.name, avatar: id.avatar };
      }
    }
    setUsersPhotos(usersInfo); // set the data we have about anonymous users immediately

    // and fetch from monday the data about known users
    getUsersForIds(idsToGet)
      .then((users) => {
        const usersInfo = {} as Record<string, UserIdInfo>;
        for (const user of users) {
          if (user) {
            usersInfo[user.id] = { type: "known", photo: user.thumbnail };
          }
        }
        setUsersPhotos((old) => ({ ...old, ...usersInfo }));
      })
      .catch((error) => {
        console.error("Failed to get users id", error);
      });
  }, [results]);

  return (
    <>
      <div className={style.title}>{session.title}</div>

      <div className={style.subTitle}>
        {activeVoters.length === 0 ? "Nobody voted" : `${activeVoters.length} voted`}
        <span data-testid="num-voters" style={{ display: "none" }}>
          {activeVoters.length}
        </span>
      </div>

      <UsersInfoContext.Provider value={usersPhotos}>
        <VoteResultsList summary={summary} showAvatars={!session.anonymous} thumbnails={thumbnailsUrls} />
      </UsersInfoContext.Provider>
    </>
  );
}

function VotingShortSummary({
  session,
  onClick,
  onMenuOpen,
  editingTitle,
  onChangeTitle,
}: {
  session: { title: string; start: number; ownerId: string };
  onClick: () => void;
  onMenuOpen: (e: any) => void;
  editingTitle: boolean;
  onChangeTitle: (t: string) => void;
  usersNames: any;
}) {
  return (
    <div className={style.shortSummary}>
      <TitleAndDate
        title={session.title}
        time={session.start}
        onClick={onClick}
        editTitle={editingTitle}
        onChangeTitle={onChangeTitle}
      />
      <div className={style.menuIcon} onClick={onMenuOpen} data-id={session.start} />
    </div>
  );
}

function TitleAndDate({
  title,
  time,
  onClick,
  extraStyle,
  editTitle,
  onChangeTitle,
}: {
  title: string;
  time: number;
  onClick?: () => void;
  extraStyle?: CSSProperties;
  editTitle?: boolean;
  onChangeTitle?: (title: string) => void;
}) {
  return (
    <div
      className={cn(style.basicContainer, style.fullDetails, !!onClick && style.pointer)}
      onClick={onClick}
      style={extraStyle}
    >
      {editTitle ? (
        <textarea
          className={cn(style.topicTextArea, style.h4)}
          defaultValue={title}
          name="topic"
          required
          rows={1}
          autoFocus={true}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key == "Escape") {
              e.currentTarget.value = title;
              e.currentTarget.blur();
            }
          }}
          onInput={(e) => {
            e.currentTarget.style.height = "0";
            e.currentTarget.style.height = e.currentTarget.scrollHeight + "px";
          }}
          onFocus={(e) => e.currentTarget.select()} // select all on activate
          onBlur={(e) => onChangeTitle && onChangeTitle(e.currentTarget.value)}
          onClick={(e) => e.stopPropagation()} // to prevent the div above us from getting the event
        />
      ) : (
        <pre className={cn(style.h4, style.wraptext)}>{title}</pre>
      )}
      <span className={style.h5}>{new Date(time).toLocaleString()}</span>
    </div>
  );
}

export function VoteResultsList({
  summary,
  showAvatars,
  thumbnails,
}: {
  summary: { [itemId: string]: { total: number; ids: VoterData["id"][] } };
  showAvatars: boolean;
  thumbnails: Record<string, string>;
}) {
  const votesSortedDescending = Object.entries(summary).sort((a, b) => b[1].total - a[1].total);
  const clearWinner = votesSortedDescending.length < 2 || votesSortedDescending[1][1] != votesSortedDescending[0][1];

  return (
    <div style={{ flex: "1 1 auto", overflowY: "auto" }}>
      {votesSortedDescending.map(([itemId, info], index) => (
        <SingleItemVoteRecord
          key={itemId}
          itemId={itemId}
          info={info}
          isWinner={index == 0 && clearWinner}
          thumbnailUrl={thumbnails[itemId]}
          showAvatars={showAvatars}
        />
      ))}
    </div>
  );
}

function SingleItemVoteRecord({
  itemId,
  isWinner,
  info: { total, ids },
  thumbnailUrl,
  showAvatars,
}: {
  itemId: string;
  isWinner: boolean;
  info: { total: number; ids: VoterData["id"][] };
  thumbnailUrl: string;
  showAvatars: boolean;
}) {
  const [signedUrl, setSignedUrl] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (thumbnailUrl) {
      getReadSignedUrl(thumbnailUrl)
        .then(({ url }) => setSignedUrl(url))
        .catch((error) => console.error("failed to get signed url", error));
    }
  }, [thumbnailUrl]);

  return (
    <div key={itemId} className={cn(style.itemResultContainer, isWinner && style.winner)}>
      {signedUrl ? (
        <Thumbnail
          src={signedUrl}
          onError={(e) => {
            if (retry <= 3) {
              const img = e.target as HTMLImageElement;
              if (!img) {
                return;
              }
              setRetry((r) => r + 1);
              setTimeout(() => {
                img.src = "";
                img.src = signedUrl;
              }, 1000 + 2 ** (1 + retry) * 500);
            }
          }}
        />
      ) : (
        <Skeleton width={60} height={60} inline={true} />
      )}
      <span className={style.number} data-testid="obj-vote-count">
        {total}
      </span>
      <span>votes</span>
      {showAvatars && <UserAvatarStack ids={ids} />}
    </div>
  );
}

function UserAvatarStack({ ids }: { ids: { id: string }[] }) {
  const usersPhotos = useContext(UsersInfoContext);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row-reverse",
        overflow: "hidden",
        alignItems: "center",
      }}
    >
      {ids.slice(0, 5).map(({ id }) => (
        <UserAvatar key={id} {...usersPhotos[id]} />
      ))}
    </div>
  );
}

function UserAvatar({ type, photo, avatar }: { type: "known" | "anonymous"; photo?: string; avatar?: string }) {
  return (
    <div
      style={{
        // display: "grid",
        // placeContent: "center",
        marginInlineStart: "-5px",
        width: "30px",
        height: "30px",
        whiteSpace: "nowrap",
        borderRadius: "50%",
        clipPath: "circle(50%)",
        border: "1px solid white",
        filter: "drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.1))",
        fontSize: "21px",
      }}
    >
      {type == "known" ? (
        <img src={photo} alt="" style={{ objectFit: "cover", objectPosition: "center", height: "100%" }} />
      ) : (
        <span>{avatar}</span>
      )}
    </div>
  );
}
